import _ from 'lodash';
import {ParsedQs} from 'qs';
import React, {FunctionComponent, useEffect, useMemo, useReducer, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {Grid} from '@material-ui/core';
import {CatalogProductStatus, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {HorizontalAnchorPosition} from '@treez-ui/core/feedback/modals/Snackbar';
import {downloadFile} from '~/lib/file';
import {
    buildProductFiltersFromParams,
    exportProducts,
    ProductNavigationAttributes,
    searchProducts,
    SearchResultsAttributes,
} from '~/lib/product';
import {
    generateQueryString,
    getProductQueryParameters,
    getSearchQueryParameters,
    parseQueryString,
} from '~/lib/routing';
import {ProductParameters, SearchQueryParameters, SortDirection} from '~/lib/sharedInterfaces';
import ProductFormDrawer from '~/modules/forms/ProductFormDrawer';
import UIWrapper, {DEFAULT_ROUTABLE_ORG_ID} from '~/modules/pages/UIWrapper';
import ProductOptions from '~/modules/pages/organizationCatalog/ProductOptions';
import {
    informUserOfErrorExportingCSV,
    informUserOfErrorFetchingProducts,
} from '~/redux/actionCreators/UserFeedbackCreators';
import {
    clearHighlightedProducts,
    highlightProduct,
} from '~/redux/actions/HighlightedProductActions';
import {selectHighlightedProducts} from '~/redux/selectors/highlightedProductsSelectors';
import {selectOrganizationCatalogId} from '~/redux/selectors/organizationSelectors';
import {SearchFilter} from '~/utils/ProductAPI';
import OrganizationProducts from './OrganizationProducts';
import OrgProductModal from './OrgProductModal';
import OrgCatalogHeader from './OrgCatalogHeader';
import {CatalogType} from '~/modules/forms/ProductForm';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';
import ProductFormContextProvider, {
    useProductFormContext,
    useProductFormStateDispatch,
} from '~/contexts/ProductForm/productFormContext';
import {openProductFormOnly, setProductToInspect} from '~/contexts/ProductForm/productFormActions';
import {
    DEFAULT_ORG_CATALOG_STATE,
    OrgCatalogContextProvider,
    orgCatalogReducer,
    setFetching,
} from '~/contexts/OrgCatalog/orgCatalogContext';

const DEFAULT_EXPORT_MAX_PER_PAGE = 1000;
const DEFAULT_PAGE_SIZE = 10;

export interface CentralCatalogPageParameters
    extends ParsedQs,
        Omit<SearchQueryParameters, 'page' | 'pageSize'>,
        ProductParameters {
    page: string;
    pageSize: string;
    hideStores?: 'true' | 'false';
}

const OrganizationCatalog: FunctionComponent<RouteComponentProps> = (
    props: RouteComponentProps
) => {
    const {history, location} = props;

    // remove tokens info from url, if exists
    const params = new URLSearchParams(location.search);
    if (params.get('tokens') !== null) {
        params.delete('tokens');
        history.replace({
            search: params.toString(),
        });
    }

    const queryParams = parseQueryString(location.search) as CentralCatalogPageParameters;
    const productStatus = queryParams.status
        ? (queryParams.status as CatalogProductStatus)
        : undefined;
    const productParameters = getProductQueryParameters(queryParams);
    const searchParameters = getSearchQueryParameters(queryParams);
    const hideStores = queryParams.hideStores === 'true';

    const {page, pageSize, searchTerm, sortCategory, sortOrder} = searchParameters;

    const dispatch = useDispatch();

    // Product States
    const [products, setProducts] = useState<ProductInCatalog[]>([]);
    const [filters, setFilters] = useState<SearchFilter[]>([]);
    const [productCount, setProductCount] = useState<number>(0);
    const [searchCount, setSearchCount] = useState<number>(0);
    const [initialFetchDone, setInitialFetchDone] = useState<boolean>(false);

    // Modal States
    const [productBeingEdited, setProductBeingEdited] = useState<ProductInCatalog | undefined>();

    const {productToInspect} = useProductFormContext();
    const productFormDispatch = useProductFormStateDispatch();

    // Catalog State
    const [productWasEdited, setProductWasEdited] = useState<boolean>(false);
    const [productWasRemoved, setProductWasRemoved] = useState<boolean>(false);

    // Context State
    const [orgCatalogSharedState, orgCatalogDispatcher] = useReducer(
        orgCatalogReducer,
        DEFAULT_ORG_CATALOG_STATE
    );
    const {fetching} = orgCatalogSharedState;

    // Global State
    const organizationCatalogId = useSelector(selectOrganizationCatalogId);
    const highlightedProducts = useSelector(selectHighlightedProducts);

    const createListOfProductsToRender = () => {
        const maxSize = !_.isNaN(Number(pageSize)) ? Number(pageSize) : DEFAULT_PAGE_SIZE;

        const updatedOrNewProductIds = _.map(highlightedProducts, (product) => {
            return _.get(product, 'id');
        });

        const productsFilteredForDuplicates = _.slice(
            _.filter(products, (product) => {
                return !_.includes(updatedOrNewProductIds, product.id);
            }),
            0,
            maxSize - highlightedProducts.length
        );

        return {
            highlightedProducts,
            otherProducts: productsFilteredForDuplicates,
        };
    };

    const productsToRender = useMemo(() => createListOfProductsToRender(), [products]);

    const receiveProducts = (response: SearchResultsAttributes) => {
        const {data, total} = response;

        setProducts(data);
        setProductCount(total);
        orgCatalogDispatcher(setFetching(false));

        if (productWasRemoved && setProductWasRemoved) {
            setProductWasRemoved(false);
            return;
        }

        if (productWasEdited && setProductWasEdited) {
            setProductWasEdited(false);
            return;
        }

        if (initialFetchDone) {
            dispatch(clearHighlightedProducts());
        }

        setInitialFetchDone(true);
    };

    const onEditFormOpen = (productToEdit?: ProductInCatalog) => {
        if (productToEdit) {
            setProductBeingEdited(productToEdit);
        }
    };

    const closeProductForm = (productAddedOrEdited?: ProductInCatalog) => {
        orgCatalogDispatcher(setFetching(true));
        if (productAddedOrEdited) {
            if (productBeingEdited) {
                const productEdit = productAddedOrEdited;
                if (productToInspect) {
                    productFormDispatch(setProductToInspect(productEdit));
                }
                setProductWasEdited(true);
            }
            dispatch(highlightProduct(productAddedOrEdited));
        }

        setProductBeingEdited(undefined);
    };

    const handleExport = async () => {
        dispatch(
            showSnackbar({
                content: <span>Exporting CSV... </span>,
                onClose: () => dispatch(clearSnackbar()),
                horizontalAnchor: HorizontalAnchorPosition.CENTER,
            })
        );

        const searchParameters = {
            filters,
            page: 1,
            pageSize: 1,
            searchTerm,
            sortCategory,
            sortOrder: sortOrder as SortDirection,
            status: productStatus ? [productStatus] : [CatalogProductStatus.ACTIVE],
        };

        const {total: totalCount} = await searchProducts(searchParameters, organizationCatalogId);

        let result;
        let page = 1;

        try {
            while (page === 1 || totalCount > page * DEFAULT_EXPORT_MAX_PER_PAGE) {
                const searchParameters = {
                    filters,
                    page,
                    pageSize: DEFAULT_EXPORT_MAX_PER_PAGE,
                    searchTerm,
                    sortCategory,
                    sortOrder: sortOrder as SortDirection,
                };

                const res = await exportProducts(searchParameters, organizationCatalogId);
                if (page === 1) {
                    result = await res;
                } else {
                    ('inside result page is not 0');
                    const headerRemoved = res.substring(res.indexOf('\n') + 1);
                    result += `\n${await headerRemoved}`;
                }

                page++;
            }

            downloadFile(result, `Product-Export-${new Date().toISOString()}.csv`);
        } catch (error) {
            console.error(error);
            dispatch(informUserOfErrorExportingCSV(error.message as string));
        } finally {
            dispatch(clearSnackbar());
        }
    };

    const handleErrorFetchingProducts = (error: Error) => {
        console.error(error);
        orgCatalogDispatcher(setFetching(false));
        setProducts([]);
        dispatch(informUserOfErrorFetchingProducts(error.message as string));
    };

    // Life Cycle Methods

    useEffect(() => {
        return () => {
            dispatch(clearHighlightedProducts());
        };
    }, []);

    useEffect(() => {
        if (pageSize == null) {
            history.replace({
                search: generateQueryString({
                    ...queryParams,
                    pageSize: DEFAULT_PAGE_SIZE,
                    page: page ?? 1,
                }),
            });

            return;
        }

        if (page == null) {
            history.replace({
                search: generateQueryString({
                    ...queryParams,
                    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
                    page: 1,
                }),
            });
        }
    }, []);

    useEffect(() => {
        if (organizationCatalogId == null || pageSize == null || page == null) {
            return;
        }
        setSearchCount(searchCount + 1);
        orgCatalogDispatcher(setFetching(true));
    }, [
        filters,
        page,
        pageSize,
        sortCategory,
        sortOrder,
        organizationCatalogId,
        searchTerm,
        productStatus,
    ]);

    useEffect(() => {
        if (fetching === false) {
            return;
        }

        const productNavigation = {
            ...searchParameters,
            status: productStatus ? [productStatus] : [CatalogProductStatus.ACTIVE],
            filters,
        } as Partial<ProductNavigationAttributes>;

        if (organizationCatalogId !== DEFAULT_ROUTABLE_ORG_ID) {
            searchProducts(productNavigation, organizationCatalogId)
                .then(receiveProducts)
                .catch(handleErrorFetchingProducts);
        } else {
            orgCatalogDispatcher(setFetching(false));
        }
    }, [fetching]);

    useEffect(() => {
        const productFilters = buildProductFiltersFromParams(productParameters);
        if (!_.isEqual(productFilters, filters)) {
            setFilters(productFilters);
        }
    }, [location.search]);

    return (
        <OrgCatalogContextProvider
            catalogGlobalState={orgCatalogSharedState}
            catalogGlobalDispatch={orgCatalogDispatcher}
        >
            <Grid container>
                <OrgCatalogHeader handleExport={handleExport} />
                <Grid item sm={12}>
                    <ProductOptions />
                </Grid>
                <Grid item sm={12}>
                    <OrganizationProducts
                        openProductForm={() => productFormDispatch(openProductFormOnly(undefined))}
                        highlightedAndRegularProducts={productsToRender}
                        productCount={productCount}
                        hideStores={hideStores}
                    />
                </Grid>
            </Grid>
            <OrgProductModal />
            <ProductFormDrawer
                anchor="right"
                catalogId={organizationCatalogId}
                catalogType={CatalogType.CENTRAL_CATALOG}
                onCloseForm={closeProductForm}
                onOpenForm={onEditFormOpen}
            />
        </OrgCatalogContextProvider>
    );
};

export const OrganizationCatalogPage: FunctionComponent<RouteComponentProps> = (
    props: RouteComponentProps
) => {
    return (
        <UIWrapper>
            <ProductFormContextProvider>
                <OrganizationCatalog {...props} />
            </ProductFormContextProvider>
        </UIWrapper>
    );
};

export default OrganizationCatalogPage;
